import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import type { GHGInventoryResult } from '../components/charts/ghgInventoryChartTypes';
import { isModeledWastewaterEstimationMethod } from './canonicalGhgInventory/wastewaterInventoryResolver';
import { sumInventoryYearChartTotal } from '../utils/sectorGhgChartTransform';
import { fetchConsumptionBasedGHGData } from './consumptionBasedGHGService';
import { fetchInventoryComparisonData } from './inventoryComparisonService';

function slugColumn(label: string): string {
  if (label.startsWith('Wastewater')) {
    return 'wastewater';
  }
  return label
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();
}

function resolveWastewaterEstimateStatusForYear(
  result: GHGInventoryResult,
  year: number
): string | null {
  const row = result.wastewaterResolvedYears?.find((r) => r.inventoryYear === year);
  if (row) {
    if (row.emissionsMt == null || row.status === 'unavailable') return null;
    if (!row.estimationMethod) return 'verified';
    if (isModeledWastewaterEstimationMethod(row.estimationMethod)) {
      return String(row.estimationMethod);
    }
    return String(row.estimationMethod);
  }
  if (isModeledWastewaterEstimationMethod(result.wastewaterEstimationMethod)) {
    return result.wastewaterEstimationMethod ?? null;
  }
  if (result.wastewaterEstimationMethod) return 'estimated';
  return null;
}

function wastewaterMtForYear(result: GHGInventoryResult, yearIndex: number): number | null {
  const wwDataset = result.chartData.datasets.find((d: { label?: string; data?: unknown[] }) =>
    String(d.label ?? '').startsWith('Wastewater')
  );
  if (!wwDataset) return null;
  const raw = wwDataset.data?.[yearIndex];
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function flattenSectorGhgInventory(location: string, result: GHGInventoryResult): Record<string, unknown>[] {
  const years = result.chartData.labels as number[];
  return years.map((year, yearIndex) => {
    const row: Record<string, unknown> = { location, year };
    for (const dataset of result.chartData.datasets) {
      const label = String(dataset.label ?? '');
      const raw = dataset.data[yearIndex];
      const col = slugColumn(label);
      if (label.startsWith('Wastewater')) {
        if (raw == null || raw === '') {
          row[col] = null;
          continue;
        }
        const value = Number(raw);
        if (!Number.isFinite(value)) {
          row[col] = null;
          continue;
        }
        row[col] = value;
        continue;
      }
      const value = raw == null || raw === '' ? 0 : Number(raw);
      const safe = Number.isFinite(value) ? value : 0;
      row[col] = safe;
    }
    const wwMt = wastewaterMtForYear(result, yearIndex);
    const resolved = result.wastewaterResolvedYears?.find((r) => r.inventoryYear === year);
    row.wastewater_mt_co2e = wwMt;
    row.wastewater_source_year = wwMt != null ? resolved?.sourceDataYear ?? result.wastewaterSourceDataYear ?? null : null;
    row.wastewater_activity_year = wwMt != null ? resolved?.activityYear ?? null : null;
    row.wastewater_estimate_status = wwMt != null ? resolveWastewaterEstimateStatusForYear(result, year) : null;
    row.total_mt_co2e = sumInventoryYearChartTotal(
      result.chartData,
      yearIndex,
      result.wastewaterSourceDataYear
    );
    return row;
  });
}

export function flattenConsumptionGhgInventory(
  location: string,
  result: Awaited<ReturnType<typeof fetchConsumptionBasedGHGData>>
): Record<string, unknown>[] {
  const years = result.chartData.labels as number[];
  return years.map((year, yearIndex) => {
    const row: Record<string, unknown> = { location, year };
    let total = 0;
    for (const dataset of result.chartData.datasets) {
      const value = Number(dataset.data[yearIndex]) || 0;
      row[slugColumn(dataset.label)] = value;
      total += value;
    }
    row.total_mt_co2e = total;
    return row;
  });
}

export function flattenInventoryComparison(
  location: string,
  result: Awaited<ReturnType<typeof fetchInventoryComparisonData>>
): Record<string, unknown>[] {
  const years = result.chartData.labels as number[];
  return years.map((year, yearIndex) => ({
    location,
    year,
    sector_based_mt_co2e: result.chartData.datasets[0]?.data[yearIndex] ?? null,
    consumption_based_mt_co2e: result.chartData.datasets[1]?.data[yearIndex] ?? null,
  }));
}

export async function loadSectorGhgInventoryDownloadRows(location: string) {
  const result = await fetchGHGInventoryData(location);
  return flattenSectorGhgInventory(location, result);
}

export async function loadConsumptionGhgInventoryDownloadRows(location: string) {
  const result = await fetchConsumptionBasedGHGData(location);
  return flattenConsumptionGhgInventory(location, result);
}

export async function loadInventoryComparisonDownloadRows(location: string) {
  const result = await fetchInventoryComparisonData(location);
  return flattenInventoryComparison(location, result);
}
